import { FreightLocationType } from "@redotech/redo-fulfillment-carrier-common/freight";
import type { FreightDeliveryAccessorialPresetRow } from "@redotech/redo-fulfillment-common/freight/freight-delivery-accessorial-preset";
import type { FreightPickupAccessorialPresetRow } from "@redotech/redo-fulfillment-common/freight/freight-pickup-accessorial-preset";
import type { FreightPickupWindowRow } from "@redotech/redo-fulfillment-common/freight/freight-pickup-window";
import { FulfillmentOrderAddress } from "@redotech/redo-fulfillment-customs-common/fulfillment-order-address";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import {
  RedoTimePicker,
  TimeOfDay,
} from "@redotech/redo-web/arbiter-components/time/redo-time-picker";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useCallback, useMemo, useState } from "react";
import { AddressCard } from "./address-card";
import { EditAddressModal } from "./edit-address-modal";
import {
  CUSTOM_DELIVERY_ACCESSORIAL_ID,
  CUSTOM_PICKUP_ACCESSORIAL_ID,
  CUSTOM_PICKUP_WINDOW_ID,
  type DeliveryAccessorialSelection,
  type PickupAccessorialSelection,
  PickupWindowSelection,
  RouteStepData,
  validateAddress,
} from "./freight-booking-types";
import * as styles from "./route-step.module.css";

function formatTime(time: Temporal.PlainTime): string {
  const period = time.hour >= 12 ? "PM" : "AM";
  const hours12 = time.hour % 12 || 12;
  return `${hours12.toString().padStart(2, "0")}:${time.minute.toString().padStart(2, "0")} ${period}`;
}

interface RouteStepProps {
  originAddress: FulfillmentOrderAddress;
  destinationAddress: FulfillmentOrderAddress;
  onOriginAddressChange(address: FulfillmentOrderAddress): void;
  onDestinationAddressChange(address: FulfillmentOrderAddress): void;
  data: RouteStepData;
  setData: (data: RouteStepData) => void;
  pickupWindows: FreightPickupWindowRow[];
  pickupAccessorialPresets: FreightPickupAccessorialPresetRow[];
  deliveryAccessorialPresets: FreightDeliveryAccessorialPresetRow[];
}

export const RouteStep = memo(function RouteStep({
  originAddress,
  destinationAddress,
  onOriginAddressChange,
  onDestinationAddressChange,
  data,
  setData,
  pickupWindows,
  pickupAccessorialPresets,
  deliveryAccessorialPresets,
}: RouteStepProps) {
  const [editingAddress, setEditingAddress] = useState<
    "origin" | "destination" | null
  >(null);
  const pickupDate = data.pickupDateTime?.toPlainDate();
  const isSameDayPickup =
    pickupDate !== undefined &&
    Temporal.PlainDate.compare(pickupDate, Temporal.Now.plainDateISO()) === 0;

  const isCustomWindow = data.pickupWindowSelection?.type === "custom";

  const pickupWindowOptions = useMemo<RedoListItem<string>[]>(() => {
    const saved = pickupWindows.map((w) => ({
      type: "text" as const,
      id: w.id,
      value: w.id,
      text: `${formatTime(w.pickupReadyTime)} - ${formatTime(w.pickupCloseTime)} (${w.name})`,
    }));
    return [
      ...saved,
      {
        type: "text" as const,
        id: CUSTOM_PICKUP_WINDOW_ID,
        value: CUSTOM_PICKUP_WINDOW_ID,
        text: "Custom pickup window",
      },
    ];
  }, [pickupWindows]);

  const selectedPickupWindowOption = useMemo(() => {
    if (!data.pickupWindowSelection) {
      return undefined;
    }
    if (data.pickupWindowSelection.type === "custom") {
      const readyTime = data.pickupDateTime?.toPlainTime();
      const text =
        readyTime && data.closeTime
          ? `${formatTime(readyTime)} - ${formatTime(data.closeTime)} (Custom)`
          : "Custom pickup window";
      return {
        type: "text" as const,
        id: CUSTOM_PICKUP_WINDOW_ID,
        value: CUSTOM_PICKUP_WINDOW_ID,
        text,
      };
    }
    return pickupWindowOptions.find(
      (o) => o.value === data.pickupWindowSelection!.id,
    );
  }, [
    data.pickupWindowSelection,
    data.pickupDateTime,
    data.closeTime,
    pickupWindowOptions,
  ]);

  const handlePickupWindowChange = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      if (item.value === CUSTOM_PICKUP_WINDOW_ID) {
        setData({
          ...data,
          pickupWindowSelection: {
            type: "custom",
            id: CUSTOM_PICKUP_WINDOW_ID,
          },
          closeTime: undefined,
          pickupDateTime: data.pickupDateTime
            ? data.pickupDateTime
                .toPlainDate()
                .toPlainDateTime({ hour: 9, minute: 0 })
            : undefined,
        });
        return;
      }
      const window = pickupWindows.find((w) => w.id === item.value);
      if (!window) {
        return;
      }
      const selection: PickupWindowSelection = { type: "saved", id: window.id };
      const date = data.pickupDateTime?.toPlainDate();
      setData({
        ...data,
        pickupWindowSelection: selection,
        pickupDateTime: date
          ? date.toPlainDateTime({
              hour: window.pickupReadyTime.hour,
              minute: window.pickupReadyTime.minute,
            })
          : undefined,
        closeTime: window.pickupCloseTime,
      });
    },
    [data, setData, pickupWindows],
  );

  const handlePickupDateChange = useCallback(
    (date: Temporal.PlainDate) => {
      if (data.pickupWindowSelection?.type === "saved") {
        const window = pickupWindows.find(
          (w) => w.id === data.pickupWindowSelection!.id,
        );
        if (window) {
          setData({
            ...data,
            pickupDateTime: date.toPlainDateTime({
              hour: window.pickupReadyTime.hour,
              minute: window.pickupReadyTime.minute,
            }),
          });
          return;
        }
      }
      const hour = data.pickupDateTime?.hour ?? 9;
      const minute = data.pickupDateTime?.minute ?? 0;
      setData({
        ...data,
        pickupDateTime: date.toPlainDateTime({ hour, minute }),
      });
    },
    [data, setData, pickupWindows],
  );

  const handlePickupTimeChange = useCallback(
    (time: TimeOfDay | null) => {
      if (!time) {
        return;
      }
      const date =
        data.pickupDateTime?.toPlainDate() ?? Temporal.Now.plainDateISO();
      setData({
        ...data,
        pickupDateTime: date.toPlainDateTime({
          hour: time.hours,
          minute: time.minutes,
        }),
      });
    },
    [data, setData],
  );

  const handleCloseTimeChange = useCallback(
    (time: TimeOfDay | null) => {
      if (!time) {
        setData({ ...data, closeTime: undefined });
        return;
      }
      setData({
        ...data,
        closeTime: new Temporal.PlainTime(time.hours, time.minutes),
      });
    },
    [data, setData],
  );

  const pickupTime: TimeOfDay | null = data.pickupDateTime
    ? { hours: data.pickupDateTime.hour, minutes: data.pickupDateTime.minute }
    : null;
  const closeTimeValue: TimeOfDay | null = data.closeTime
    ? { hours: data.closeTime.hour, minutes: data.closeTime.minute }
    : null;

  const originAddressError = useMemo(
    () => validateAddress(originAddress, "Origin address"),
    [originAddress],
  );
  const destinationAddressError = useMemo(
    () => validateAddress(destinationAddress, "Destination address"),
    [destinationAddress],
  );

  const handlePickupLocationTypeChange = useCallback(
    (locationType: FreightLocationType) => {
      setData({ ...data, pickupLocationType: locationType });
    },
    [data, setData],
  );

  const handleDeliveryLocationTypeChange = useCallback(
    (locationType: FreightLocationType) => {
      setData({ ...data, deliveryLocationType: locationType });
    },
    [data, setData],
  );

  const isPickupAccessorialCustom =
    data.pickupAccessorialSelection?.type === "custom";
  const isDeliveryAccessorialCustom =
    data.deliveryAccessorialSelection?.type === "custom";

  const pickupAccessorialOptions = useMemo<RedoListItem<string>[]>(() => {
    const saved = pickupAccessorialPresets.map((p) => ({
      type: "text" as const,
      id: p.id,
      value: p.id,
      text: p.name,
    }));
    return [
      ...saved,
      {
        type: "text" as const,
        id: CUSTOM_PICKUP_ACCESSORIAL_ID,
        value: CUSTOM_PICKUP_ACCESSORIAL_ID,
        text: "Custom",
      },
    ];
  }, [pickupAccessorialPresets]);

  const selectedPickupAccessorialOption = useMemo(() => {
    if (!data.pickupAccessorialSelection) {
      return undefined;
    }
    return pickupAccessorialOptions.find(
      (o) => o.value === data.pickupAccessorialSelection!.id,
    );
  }, [data.pickupAccessorialSelection, pickupAccessorialOptions]);

  const handlePickupAccessorialChange = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      if (item.value === CUSTOM_PICKUP_ACCESSORIAL_ID) {
        setData({
          ...data,
          pickupAccessorialSelection: {
            type: "custom",
            id: CUSTOM_PICKUP_ACCESSORIAL_ID,
          },
        });
        return;
      }
      const preset = pickupAccessorialPresets.find((p) => p.id === item.value);
      if (!preset) {
        return;
      }
      const selection: PickupAccessorialSelection = {
        type: "saved",
        id: preset.id,
      };
      setData({
        ...data,
        pickupAccessorialSelection: selection,
        pickupAccessorials: {
          liftgateRequired:
            preset.liftgateRequired ||
            data.pickupLocationType === FreightLocationType.RESIDENTIAL,
          insidePickup: preset.insidePickup,
          instructions: preset.instructions ?? "",
        },
      });
    },
    [data, setData, pickupAccessorialPresets],
  );

  const deliveryAccessorialOptions = useMemo<RedoListItem<string>[]>(() => {
    const saved = deliveryAccessorialPresets.map((p) => ({
      type: "text" as const,
      id: p.id,
      value: p.id,
      text: p.name,
    }));
    return [
      ...saved,
      {
        type: "text" as const,
        id: CUSTOM_DELIVERY_ACCESSORIAL_ID,
        value: CUSTOM_DELIVERY_ACCESSORIAL_ID,
        text: "Custom",
      },
    ];
  }, [deliveryAccessorialPresets]);

  const selectedDeliveryAccessorialOption = useMemo(() => {
    if (!data.deliveryAccessorialSelection) {
      return undefined;
    }
    return deliveryAccessorialOptions.find(
      (o) => o.value === data.deliveryAccessorialSelection!.id,
    );
  }, [data.deliveryAccessorialSelection, deliveryAccessorialOptions]);

  const handleDeliveryAccessorialChange = useCallback(
    ({ item }: { item: RedoListItem<string> }) => {
      if (item.value === CUSTOM_DELIVERY_ACCESSORIAL_ID) {
        setData({
          ...data,
          deliveryAccessorialSelection: {
            type: "custom",
            id: CUSTOM_DELIVERY_ACCESSORIAL_ID,
          },
        });
        return;
      }
      const preset = deliveryAccessorialPresets.find(
        (p) => p.id === item.value,
      );
      if (!preset) {
        return;
      }
      const selection: DeliveryAccessorialSelection = {
        type: "saved",
        id: preset.id,
      };
      setData({
        ...data,
        deliveryAccessorialSelection: selection,
        deliveryAccessorials: {
          liftgateRequired:
            preset.liftgateRequired ||
            data.deliveryLocationType === FreightLocationType.RESIDENTIAL,
          insideDelivery: preset.insideDelivery,
          appointmentRequired: preset.appointmentRequired,
          instructions: preset.instructions ?? "",
        },
      });
    },
    [data, setData, deliveryAccessorialPresets],
  );

  const handlePickupInstructionsChange = useCallback(
    (value: string) => {
      setData({
        ...data,
        pickupAccessorials: { ...data.pickupAccessorials, instructions: value },
      });
    },
    [data, setData],
  );

  const handleDeliveryInstructionsChange = useCallback(
    (value: string) => {
      setData({
        ...data,
        deliveryAccessorials: {
          ...data.deliveryAccessorials,
          instructions: value,
        },
      });
    },
    [data, setData],
  );

  return (
    <Flex gap="3xl" justify="center" p="3xl" w="full">
      {/* Origin column */}
      <Flex className={styles.column} dir="column" gap="lg" grow={1}>
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Origin
        </Text>
        <Card p="xl" radius="md">
          <AddressCard
            address={originAddress}
            locationType={data.pickupLocationType}
            onEdit={() => setEditingAddress("origin")}
            onLocationTypeChange={handlePickupLocationTypeChange}
            title="Pickup location"
            warning={originAddressError}
          />
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Pickup date
            </Text>
            <RedoSingleDatePicker
              buttonSize="xs"
              date={pickupDate}
              minDate={Temporal.Now.plainDateISO()}
              setDate={handlePickupDateChange}
            />
            {isSameDayPickup && (
              <Text fontSize="xs" textColor="tertiary">
                If the carrier is unable to pick the shipment up in time, the
                pickup will be rescheduled for the following business day.
              </Text>
            )}
          </Flex>
          <RedoSingleSelectDropdownInput
            label="Pickup window"
            options={pickupWindowOptions}
            optionSelected={handlePickupWindowChange}
            placeholder="Select a pickup window"
            selectedItem={selectedPickupWindowOption}
            size="xs"
          />
          {isCustomWindow && (
            <Flex dir="row" gap="sm">
              <Flex dir="column" gap="xs" grow={1}>
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Ready time
                </Text>
                <RedoTimePicker
                  className={styles.timePicker}
                  setValue={handlePickupTimeChange}
                  size="xs"
                  value={pickupTime}
                />
              </Flex>
              <Flex dir="column" gap="xs" grow={1}>
                <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                  Close time
                </Text>
                <RedoTimePicker
                  className={styles.timePicker}
                  setValue={handleCloseTimeChange}
                  size="xs"
                  value={closeTimeValue}
                />
              </Flex>
            </Flex>
          )}
          <Flex className={styles.divider} my="xl" />
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Pickup accessorials
          </Text>
          <RedoSingleSelectDropdownInput
            options={pickupAccessorialOptions}
            optionSelected={handlePickupAccessorialChange}
            placeholder="Select a preset"
            selectedItem={selectedPickupAccessorialOption}
            size="xs"
          />
          <Flex dir="column" gap="sm">
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="sm">
                <RedoCheckbox
                  disabled={!isPickupAccessorialCustom}
                  label="Liftgate required"
                  setValue={(val) =>
                    setData({
                      ...data,
                      pickupAccessorials: {
                        ...data.pickupAccessorials,
                        liftgateRequired: val === true,
                      },
                    })
                  }
                  size="xs"
                  value={data.pickupAccessorials.liftgateRequired}
                />
                {originAddress.residential === true && (
                  <Text fontSize="xxs" textColor="tertiary">
                    Auto-selected for residential address
                  </Text>
                )}
              </Flex>
              <RedoCheckbox
                disabled={!isPickupAccessorialCustom}
                label="Inside pickup"
                setValue={(val) =>
                  setData({
                    ...data,
                    pickupAccessorials: {
                      ...data.pickupAccessorials,
                      insidePickup: val === true,
                    },
                  })
                }
                size="xs"
                value={data.pickupAccessorials.insidePickup}
              />
            </Flex>
          </Flex>
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Pickup instructions
            </Text>
            <RedoTextareaInputField
              containerHeight="100px"
              disabled={!isPickupAccessorialCustom}
              maxLength={60}
              placeholder="Enter special instructions..."
              setValue={handlePickupInstructionsChange}
              value={data.pickupAccessorials.instructions ?? ""}
            />
          </Flex>
        </Card>
      </Flex>

      {/* Destination column */}
      <Flex className={styles.column} dir="column" gap="lg" grow={1}>
        <Text fontSize="md" fontWeight="semibold" textColor="primary">
          Destination
        </Text>
        <Card p="xl" radius="md">
          <AddressCard
            address={destinationAddress}
            locationType={data.deliveryLocationType}
            onEdit={() => setEditingAddress("destination")}
            onLocationTypeChange={handleDeliveryLocationTypeChange}
            title="Delivery location"
            warning={destinationAddressError}
          />
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Delivery accessorials
          </Text>
          <RedoSingleSelectDropdownInput
            options={deliveryAccessorialOptions}
            optionSelected={handleDeliveryAccessorialChange}
            placeholder="Select a preset"
            selectedItem={selectedDeliveryAccessorialOption}
            size="xs"
          />
          <Flex dir="column" gap="sm">
            <Flex dir="column" gap="sm">
              <Flex align="center" gap="sm">
                <RedoCheckbox
                  disabled={!isDeliveryAccessorialCustom}
                  label="Liftgate required"
                  setValue={(val) =>
                    setData({
                      ...data,
                      deliveryAccessorials: {
                        ...data.deliveryAccessorials,
                        liftgateRequired: val === true,
                      },
                    })
                  }
                  size="xs"
                  value={data.deliveryAccessorials.liftgateRequired}
                />
                {destinationAddress.residential === true && (
                  <Text fontSize="xxs" textColor="tertiary">
                    Auto-selected for residential address
                  </Text>
                )}
              </Flex>
              <RedoCheckbox
                disabled={!isDeliveryAccessorialCustom}
                label="Inside delivery"
                setValue={(val) =>
                  setData({
                    ...data,
                    deliveryAccessorials: {
                      ...data.deliveryAccessorials,
                      insideDelivery: val === true,
                    },
                  })
                }
                size="xs"
                value={data.deliveryAccessorials.insideDelivery}
              />
              <RedoCheckbox
                disabled={!isDeliveryAccessorialCustom}
                label="Appointment required"
                setValue={(val) =>
                  setData({
                    ...data,
                    deliveryAccessorials: {
                      ...data.deliveryAccessorials,
                      appointmentRequired: val === true,
                    },
                  })
                }
                size="xs"
                value={data.deliveryAccessorials.appointmentRequired}
              />
            </Flex>
          </Flex>
          <Flex dir="column" gap="sm">
            <Text fontSize="xs" fontWeight="medium" textColor="secondary">
              Delivery instructions
            </Text>
            <RedoTextareaInputField
              containerHeight="100px"
              disabled={!isDeliveryAccessorialCustom}
              maxLength={500}
              placeholder="Enter special instructions..."
              setValue={handleDeliveryInstructionsChange}
              value={data.deliveryAccessorials.instructions ?? ""}
            />
          </Flex>
        </Card>
      </Flex>
      <EditAddressModal
        address={
          editingAddress === "origin" ? originAddress : destinationAddress
        }
        isOpen={editingAddress !== null}
        onClose={() => setEditingAddress(null)}
        onSave={
          editingAddress === "origin"
            ? onOriginAddressChange
            : onDestinationAddressChange
        }
        title={
          editingAddress === "origin"
            ? "Edit origin address"
            : "Edit destination address"
        }
      />
    </Flex>
  );
});
